// Where visitors drop off in the quote engine, as Google Analytics events.
// Each step is sent once per page load, so a funnel report in Analytics reads
// as people, not clicks:
//   calc_start        first change to the figures or answers
//   calc_results_seen the comparison table (or "no matches") scrolled into view
//   apply_open        "Get exact terms" / "Let an adviser find a product" opened
//   apply_form_start  first field typed in that form
//   generate_lead     the form sent (apply.ts), or a callback request (method: callback)
// plus callback_request and email_results. Like every event, these only reach
// Analytics once the visitor accepts cookies (public-shared/consent.js), and
// nothing is sent from the admin preview, which has no Analytics.

declare global {
  interface Window { gtag?: (...args: unknown[]) => void }
}

const sent = new Set<string>();

/** Sends an Analytics event (never throws). */
export function track(name: string, params: Record<string, unknown> = {}): void {
  try {
    window.gtag?.('event', name, params);
  } catch {
    // Analytics never gets in the way of the form.
  }
}

/** Sends an event the first time only, this page load. */
export function trackOnce(name: string, params: Record<string, unknown> = {}): void {
  if (sent.has(name)) return;
  sent.add(name);
  track(name, params);
}

/** Sends `name` once when `el` is first at least a third on screen. */
export function trackWhenSeen(el: Element, name: string, params: Record<string, unknown> = {}): void {
  if (typeof IntersectionObserver === 'undefined') return;
  const observer = new IntersectionObserver((entries) => {
    if (!entries.some((e) => e.isIntersecting)) return;
    observer.disconnect();
    trackOnce(name, params);
  }, { threshold: 0.33 });
  observer.observe(el);
}
